import React, { useState, useEffect } from "react";
import axios from "axios";
import TopHeader from "../../../components/dashboard/user-dashboard/TopHeader";
import loader from "../../../assets/images/loader.gif";

const AdminDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [formError, setFormError] = useState("");

  // Form Fields
  const [code, setCode] = useState("");
  const [percentage, setPercentage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [selectedCycles, setSelectedCycles] = useState([]);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const plansList = ["copper", "silver", "gold", "platinum", "titanium"];
  const cyclesList = ["monthly", "yearly"];

  useEffect(() => {
    fetchDiscounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const fetchDiscounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("accessToken");
      let url = `${BASE_URL}admin/discounts?page=${page}&limit=10`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setDiscounts(response.data.data || []);
        if (response.data.meta) {
          setTotalPages(response.data.meta.totalPages || 1);
          setTotalItems(response.data.meta.total || 0);
        }
      }
    } catch (err) {
      console.error("Error fetching discounts:", err);
      setError(err.response?.data?.message || "Failed to load discount codes.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDiscounts();
  };

  const handleStatusToggle = async (discountId, currentStatus) => {
    try {
      const token = localStorage.getItem("accessToken");
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      
      const response = await axios.put(
        `${BASE_URL}admin/discounts/${discountId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setDiscounts((prev) =>
          prev.map((d) =>
            d.id === discountId ? { ...d, status: newStatus } : d
          )
        );
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert(err.response?.data?.message || "Failed to update status.");
    }
  };

  const handleDelete = async (discountId) => {
    if (!window.confirm("Are you sure you want to delete/archive this discount code?")) {
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.delete(`${BASE_URL}admin/discounts/${discountId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        fetchDiscounts();
      }
    } catch (err) {
      console.error("Error deleting discount:", err);
      alert(err.response?.data?.message || "Failed to delete discount.");
    }
  };

  const handlePlanCheckboxChange = (plan) => {
    setSelectedPlans((prev) =>
      prev.includes(plan) ? prev.filter((p) => p !== plan) : [...prev, plan]
    );
  };

  const handleCycleCheckboxChange = (cycle) => {
    setSelectedCycles((prev) =>
      prev.includes(cycle) ? prev.filter((c) => c !== cycle) : [...prev, cycle]
    );
  };

  const validateForm = () => {
    const errors = {};
    if (!code.trim()) errors.code = "Discount code is required.";
    else if (!/^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ]+$/.test(code)) {
      errors.code = "Code must contain only alphanumeric characters, no spaces.";
    } else if (code.length < 3 || code.length > 50) {
      errors.code = "Code must be between 3 and 50 characters.";
    }

    if (!percentage) errors.percentage = "Percentage is required.";
    else {
      const pct = parseInt(percentage, 10);
      if (isNaN(pct) || pct < 1 || pct > 100) {
        errors.percentage = "Percentage must be between 1 and 100.";
      }
    }

    if (!startDate) errors.startDate = "Start date is required.";
    if (!endDate) errors.endDate = "End date is required.";
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      errors.endDate = "End date must be after start date.";
    }

    if (selectedPlans.length === 0) {
      errors.plans = "At least one applicable plan must be selected.";
    }

    if (selectedCycles.length === 0) {
      errors.cycles = "At least one billing cycle must be selected.";
    }

    if (maxRedemptions !== "") {
      const mr = parseInt(maxRedemptions, 10);
      if (isNaN(mr) || mr < 1) {
        errors.maxRedemptions = "Max redemptions must be at least 1.";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateDiscountSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setValidationErrors({});

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const payload = {
        code,
        percentage: parseInt(percentage, 10),
        start_date: startDate,
        end_date: endDate,
        applicable_plans: selectedPlans,
        billing_cycles: selectedCycles,
        max_redemptions: maxRedemptions ? parseInt(maxRedemptions, 10) : null,
      };

      const response = await axios.post(`${BASE_URL}admin/discounts`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data && !response.data.isError) {
        setIsModalOpen(false);
        resetFormFields();
        fetchDiscounts();
      } else if (response.data && response.data.isError) {
        const errors = {};
        if (response.data.errorMessage) {
          response.data.errorMessage.forEach((err) => {
            if (err.field) {
              errors[err.field] = err.message;
            }
          });
        }
        setValidationErrors(errors);
        setFormError(response.data.message || "Failed to create discount.");
      }
    } catch (err) {
      console.error("Error creating discount:", err);
      if (err.response?.data?.errorMessage) {
        const errors = {};
        err.response.data.errorMessage.forEach((e) => {
          if (e.field) {
            errors[e.field] = e.message;
          }
        });
        setValidationErrors(errors);
      }
      setFormError(err.response?.data?.message || "Failed to create discount. Please check values.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetFormFields = () => {
    setCode("");
    setPercentage("");
    setStartDate("");
    setEndDate("");
    setMaxRedemptions("");
    setSelectedPlans([]);
    setSelectedCycles([]);
    setValidationErrors({});
    setFormError("");
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <>
      <TopHeader />
      <div className="container mx-auto px-4 py-6 font-sans">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Discount Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create and manage promotional discount codes integrated with Stripe.
            </p>
          </div>
          <button
            onClick={() => {
              resetFormFields();
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-semibold transition focus:outline-none focus:ring-4 focus:ring-blue-300"
          >
            Create Discount
          </button>
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-6">
          <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code (e.g. SUMMERSALE)..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-white text-sm font-medium rounded-lg transition"
              >
                Search
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </form>
        </div>

        {/* Loading / Error States */}
        {loading ? (
          <div className="flex justify-center my-10">
            <img src={loader} alt="Loading" className="w-16 h-16" />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg my-6 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        ) : discounts.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow">
            <p className="text-gray-500 dark:text-gray-400">No discount codes found matching the filters.</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300 text-center">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left">Code</th>
                    <th scope="col" className="px-6 py-4">Discount</th>
                    <th scope="col" className="px-6 py-4">Valid Period</th>
                    <th scope="col" className="px-6 py-4 text-left">Plans</th>
                    <th scope="col" className="px-6 py-4">Cycles</th>
                    <th scope="col" className="px-6 py-4">Redemptions</th>
                    <th scope="col" className="px-6 py-4">Status</th>
                    <th scope="col" className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {discounts.map((discount) => (
                    <tr
                      key={discount.id}
                      className="bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700/50 text-center text-gray-800 dark:text-gray-300"
                    >
                      <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white text-left">
                        {discount.code}
                      </td>
                      <td className="px-6 py-4 text-blue-600 dark:text-blue-400 font-bold">
                        {discount.percentage}%
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div>From: {discount.start_date}</div>
                        <div>To: {discount.end_date}</div>
                      </td>
                      <td className="px-6 py-4 text-left text-xs capitalize">
                        <div className="flex flex-wrap gap-1">
                          {discount.applicable_plans?.map((plan) => (
                            <span
                              key={plan}
                              className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
                            >
                              {plan}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs capitalize">
                        {discount.billing_cycles?.join(", ")}
                      </td>
                      <td className="px-6 py-4 font-medium text-xs">
                        {discount.current_redemptions || 0} /{" "}
                        {discount.max_redemptions ? discount.max_redemptions : "∞"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
                            discount.status === "active"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                              : discount.status === "inactive"
                              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                              : discount.status === "pending"
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                          }`}
                        >
                          {discount.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3">
                          {discount.status !== "archived" && (
                            <button
                              onClick={() => handleStatusToggle(discount.id, discount.status)}
                              className={`text-xs px-2.5 py-1 rounded border hover:shadow transition font-medium ${
                                discount.status === "active"
                                  ? "border-amber-300 text-amber-600 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400"
                                  : "border-green-300 text-green-600 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
                              }`}
                            >
                              {discount.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(discount.id)}
                            className="text-xs px-2.5 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 hover:shadow transition font-medium dark:border-red-800 dark:text-red-400"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalItems > 10 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, totalItems)} of {totalItems} records
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-xs font-semibold rounded transition"
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded transition ${
                        page === p
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-xs font-semibold rounded transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal: Create Discount */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg my-8 overflow-hidden">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Create New Discount Code</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateDiscountSubmit} className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-3 rounded text-red-700 dark:text-red-300 text-xs">
                    {formError}
                  </div>
                )}

                {/* Code Field */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                    Discount Code *
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="e.g. BLACKFRIDAY50"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none uppercase"
                  />
                  {validationErrors.code && (
                    <span className="text-xs text-red-500 mt-1 block">{validationErrors.code}</span>
                  )}
                </div>

                {/* Percentage & Max Redemptions Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                      Percentage Off *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={percentage}
                      onChange={(e) => setPercentage(e.target.value)}
                      placeholder="e.g. 50"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {validationErrors.percentage && (
                      <span className="text-xs text-red-500 mt-1 block">{validationErrors.percentage}</span>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                      Max Redemptions (Optional)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={maxRedemptions}
                      onChange={(e) => setMaxRedemptions(e.target.value)}
                      placeholder="Unlimited if empty"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {validationErrors.maxRedemptions && (
                      <span className="text-xs text-red-500 mt-1 block">{validationErrors.maxRedemptions}</span>
                    )}
                  </div>
                </div>

                {/* Dates Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {validationErrors.startDate && (
                      <span className="text-xs text-red-500 mt-1 block">{validationErrors.startDate}</span>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {validationErrors.endDate && (
                      <span className="text-xs text-red-500 mt-1 block">{validationErrors.endDate}</span>
                    )}
                  </div>
                </div>

                {/* Applicable Plans Checkboxes */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                    Applicable Plans *
                  </label>
                  <div className="flex flex-wrap gap-4 mt-2">
                    {plansList.map((plan) => (
                      <label key={plan} className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300 capitalize cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPlans.includes(plan)}
                          onChange={() => handlePlanCheckboxChange(plan)}
                          className="rounded text-blue-600 focus:ring-blue-500 mr-2"
                        />
                        {plan}
                      </label>
                    ))}
                  </div>
                  {validationErrors.plans && (
                    <span className="text-xs text-red-500 mt-1 block">{validationErrors.plans}</span>
                  )}
                </div>

                {/* Applicable Cycles Checkboxes */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-1">
                    Billing Cycles *
                  </label>
                  <div className="flex gap-4 mt-2">
                    {cyclesList.map((cycle) => (
                      <label key={cycle} className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300 capitalize cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCycles.includes(cycle)}
                          onChange={() => handleCycleCheckboxChange(cycle)}
                          className="rounded text-blue-600 focus:ring-blue-500 mr-2"
                        />
                        {cycle}
                      </label>
                    ))}
                  </div>
                  {validationErrors.cycles && (
                    <span className="text-xs text-red-500 mt-1 block">{validationErrors.cycles}</span>
                  )}
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    disabled={submitting}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {submitting ? "Creating..." : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminDiscounts;
